import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { FaTable, FaChartLine, FaApple, FaAndroid } from "react-icons/fa";
import AdminService from "../../services/adminService";

// Validated categorical slots (blue / aqua / orange); same entity, same color in both charts.
const COLORS = {
  trial: "#2a78d6",
  paid: "#1baf7a",
  promotional: "#8b5cf6",
  expired: "#eb6834",
  other: "#9ca3af",
};

const SERIES = [
  { key: "trialsStarted", name: "Trials started", color: COLORS.trial },
  { key: "paidStarted", name: "Became paid", color: COLORS.paid },
  { key: "expired", name: "Expired", color: COLORS.expired },
];

const GRAINS = [
  { key: "day", label: "Day", title: "Last 30 days" },
  { key: "week", label: "Week", title: "Last 12 weeks" },
  { key: "month", label: "Month", title: "Last 12 months" },
  { key: "year", label: "Year", title: "Last 5 years" },
  { key: "custom", label: "Custom" },
];

const COLUMN = { day: "Day", week: "Week of", month: "Month", year: "Year" };

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const defaultRange = () => {
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: ymd(from), to: ymd(new Date()) };
};

const toDate = (period) => {
  const [y, m, d = 1] = period.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const shortDate = (period) =>
  toDate(period).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });

const axisLabel = (period, grain) => {
  if (grain === "year") return period;
  return grain === "month"
    ? toDate(period).toLocaleString("en-US", { month: "short", year: "2-digit" })
    : toDate(period).toLocaleString("en-US", { month: "short", day: "numeric" });
};

// Every point gets a label; day view shows the day number, with the month on a
// second line at the start and at each month boundary, so 30 days fit.
const axisTick = (period, grain, index) => {
  if (grain !== "day") return [axisLabel(period, grain), null];
  const date = toDate(period);
  const month = date.toLocaleString("en-US", { month: "short" });
  return [String(date.getDate()), index === 0 || date.getDate() === 1 ? month : null];
};

const fullLabel = (period, grain) => {
  if (grain === "year") return period;
  const date = toDate(period);
  if (grain === "month") return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  const day = date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return grain === "week" ? `Week of ${day}` : day;
};

const Card = ({ title, subtitle, action, children }) => (
  <div className="h-full bg-white rounded-xl border border-gray-200/70 shadow-sm p-5">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg text-xs min-w-[180px]">
      <p className="font-semibold text-gray-900 mb-1.5">{payload[0].payload.fullLabel}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6 py-0.5">
          <span className="flex items-center gap-2 text-gray-600">
            <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-semibold text-gray-900 tabular-nums">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const TrendChart = ({ trends }) => {
  const [asTable, setAsTable] = useState(false);
  const [grain, setGrain] = useState("month");
  const [draft, setDraft] = useState(defaultRange);
  const [custom, setCustom] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState("");

  const today = ymd(new Date());
  const isCustom = grain === "custom";
  const current = GRAINS.find((g) => g.key === grain);
  const activeGrain = isCustom ? custom?.grain || "day" : grain;
  const series = isCustom ? custom?.series || [] : trends[grain] || [];
  const lastIsCurrent = !isCustom || (custom && custom.to >= today);
  const data = series.map((t, i) => ({
    ...t,
    label: axisLabel(t.period, activeGrain),
    tick: axisTick(t.period, activeGrain, i),
    fullLabel: `${fullLabel(t.period, activeGrain)}${
      lastIsCurrent && i === series.length - 1 ? " (so far)" : ""
    }`,
  }));

  const renderTick = ({ x, y, payload }) => {
    const [top, bottom] = data[payload.index]?.tick || [payload.value, null];
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={12} textAnchor="middle" fontSize={11} fill="#6b7280">{top}</text>
        {bottom && (
          <text dy={25} textAnchor="middle" fontSize={10} fontWeight={600} fill="#374151">{bottom}</text>
        )}
      </g>
    );
  };

  const applyCustom = async (e) => {
    e?.preventDefault();
    if (!draft.from || !draft.to) return setCustomError("Pick both dates.");
    if (draft.from > draft.to) return setCustomError("From must be on or before To.");
    setCustomError("");
    setCustomLoading(true);
    try {
      setCustom(await AdminService.fetchSubscriptionTrend(draft.from, draft.to));
    } catch (err) {
      setCustomError(err.response?.data?.message || "Could not load this date range.");
    } finally {
      setCustomLoading(false);
    }
  };

  const selectGrain = (key) => {
    setGrain(key);
    if (key === "custom" && !custom && !customLoading) applyCustom();
  };

  const subtitle = isCustom
    ? custom
      ? `${shortDate(custom.from)} – ${shortDate(custom.to)} · by ${custom.grain}`
      : "Pick a date range"
    : current.title;
  const totals = Object.fromEntries(
    SERIES.map((s) => [s.key, data.reduce((sum, row) => sum + (row[s.key] || 0), 0)]),
  );
  const empty = SERIES.every((s) => totals[s.key] === 0);
  const dense = data.length > 12;

  return (
    <Card
      title="Activity"
      subtitle={subtitle}
      action={
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-gray-100 p-0.5" role="group" aria-label="Group by">
            {GRAINS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => selectGrain(g.key)}
                aria-pressed={grain === g.key}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  grain === g.key
                    ? "bg-white text-gray-900 shadow-sm font-medium"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setAsTable((v) => !v)}
            title={asTable ? "Show chart" : "Show table"}
            aria-label={asTable ? "Show chart" : "Show table"}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            {asTable ? <FaChartLine /> : <FaTable />}
          </button>
        </div>
      }
    >
      {isCustom && (
        <form
          onSubmit={applyCustom}
          className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg bg-gray-50 border border-gray-100"
        >
          <label className="text-xs font-medium text-gray-500">
            From
            <input
              type="date"
              value={draft.from}
              max={draft.to || today}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
              className="mt-1 block rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </label>
          <label className="text-xs font-medium text-gray-500">
            To
            <input
              type="date"
              value={draft.to}
              min={draft.from}
              max={today}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
              className="mt-1 block rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </label>
          <button
            type="submit"
            disabled={customLoading}
            className="px-4 py-1.5 text-sm font-medium rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
          >
            {customLoading ? "Loading…" : "Apply"}
          </button>
          {customError && <span className="text-xs text-red-600 self-center">{customError}</span>}
        </form>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-2">
            <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: s.color }} />
            <span className="text-xs text-gray-500">{s.name}</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">{totals[s.key]}</span>
          </div>
        ))}
      </div>

      {asTable ? (
        <div className="overflow-auto max-h-64 rounded-lg border border-gray-100">
          <table className="w-full text-sm text-gray-700">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="text-left text-xs text-gray-500">
                <th className="py-2 px-3 font-medium">{COLUMN[activeGrain]}</th>
                {SERIES.map((s) => (
                  <th key={s.key} className="py-2 px-3 font-medium text-right">{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...data].reverse().map((row) => (
                <tr key={row.period} className="border-t border-gray-100">
                  <td className="py-1.5 px-3">{row.label}</td>
                  {SERIES.map((s) => (
                    <td key={s.key} className="py-1.5 px-3 text-right tabular-nums">{row[s.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 20, bottom: 0, left: -20 }}>
              <CartesianGrid vertical={false} stroke="#eceae6" />
              <XAxis
                dataKey="period"
                interval={data.length <= 31 ? 0 : "preserveStartEnd"}
                tick={renderTick}
                height={activeGrain === "day" ? 36 : 24}
                padding={{ left: 12, right: 12 }}
                axisLine={{ stroke: "#d4d2cc" }}
                tickLine={false}
                minTickGap={4}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#9ca3af", strokeDasharray: "3 3" }} />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="linear"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={dense ? false : { r: 4, strokeWidth: 2, stroke: "#ffffff", fill: s.color }}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: "#ffffff" }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {empty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="rounded-md bg-white/90 px-3 py-1.5 text-xs text-gray-500 border border-gray-200">
                {customLoading ? "Loading…" : "No activity in this period"}
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const SplitCard = ({ stats }) => {
  const byStatus = stats.byStatus || {};
  const byPlatform = stats.byPlatform || {};
  const trial = stats.onTrial || 0;
  const paid = stats.paidActive || 0;
  const promotional = stats.promotionalActive || 0;
  const expired = byStatus.expired || 0;
  const other = Math.max(stats.total - trial - paid - promotional - expired, 0);

  const parts = [
    { name: "Paid", value: paid, color: COLORS.paid },
    { name: "Trial", value: trial, color: COLORS.trial },
    { name: "Expired", value: expired, color: COLORS.expired },
    { name: "Free (promotional)", value: promotional, color: COLORS.promotional },
    { name: "Refunded / other", value: other, color: COLORS.other },
  ].filter((p, i) => i < 3 || p.value > 0);

  const pct = (v) => (stats.total ? Math.round((v / stats.total) * 100) : 0);
  const ios = byPlatform.ios || 0;
  const android = byPlatform.android || 0;

  return (
    <Card title="Current split" subtitle={`${stats.total.toLocaleString("en-US")} subscriptions`}>
      {stats.total === 0 ? (
        <p className="text-sm text-gray-500">No subscriptions yet.</p>
      ) : (
        <>
          <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full" role="img" aria-label="Subscription split">
            {parts
              .filter((p) => p.value > 0)
              .map((p) => (
                <div
                  key={p.name}
                  title={`${p.name}: ${p.value} (${pct(p.value)}%)`}
                  className="h-full"
                  style={{ width: `${(p.value / stats.total) * 100}%`, backgroundColor: p.color }}
                />
              ))}
          </div>

          <ul className="mt-5 divide-y divide-gray-100">
            {parts.map((p) => (
              <li key={p.name} className="flex items-center justify-between py-2.5 text-sm">
                <span className="flex items-center gap-2.5 text-gray-600">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
                  {p.name}
                </span>
                <span className="flex items-baseline gap-2 tabular-nums">
                  <span className="font-semibold text-gray-900">{p.value.toLocaleString("en-US")}</span>
                  <span className="w-10 text-right text-xs text-gray-400">{pct(p.value)}%</span>
                </span>
              </li>
            ))}
          </ul>

          {ios + android > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-5 text-xs text-gray-500">
              <span className="font-medium text-gray-600">Active by platform</span>
              <span className="flex items-center gap-1.5"><FaApple /> {ios}</span>
              <span className="flex items-center gap-1.5"><FaAndroid /> {android}</span>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const SubscriptionCharts = ({ stats }) => (
  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
    <div className="xl:col-span-2">
      <TrendChart trends={stats.trends || {}} />
    </div>
    <SplitCard stats={stats} />
  </div>
);

export default SubscriptionCharts;
